// The rules for an EDITED WhatsApp message (Phase 2, 2026-10-10): what a practice may type, and what we
// send to WhatsApp for approval.
//
// Design: teamio-serverless-api/docs/plans/2026-10-08-whatsapp-message-templates-design.md ("Validator").
// MIRRORED by teamio-serverless-api/conversations-service/lib/waOnboarding/templateRules.js - one file
// cannot be shared across repos (this one is ESM, that one is CommonJS). The two are bound by
// cypress/node-tests/whatsapp-template-rules-agreement.test.js, which runs one case corpus through both.
// The SERVER is the real gate; this copy gives instant feedback while the practice types.
//
// This is a config that GATES A SEND: an invalid message must be UNSAVEABLE, never stored with a success
// toast. So the verdict is a plain `ok` plus `errors` (refuse the save) and `warnings` (saveable).
//
// What the practice types: ordinary text with BLANKS written as [Practice name], [Appointment date and
// time] and [Office phone] - the same words the Phase 1 card already shows (splitBlanks highlights them).
// On save each blank is numbered {{1}}, {{2}}... IN THE ORDER IT APPEARS, and the position -> variable map
// is stored with the message. Nothing else about the send path changes.
//
// HARD (errors) - our own rules, each with a reason:
//   * only the three allowed blanks (minimal-PHI: a patient identifier can never be wired in)
//   * wording of its own (a message of nothing but blanks says nothing)
//   * no curly braces pair (Twilio would read {{x}} as a variable and number it for us)
//   * a sanity length cap (our own input guard, not a Meta claim)
//   * a blank at the very start or end of the message (spaces and punctuation do not count as words). PROVEN
//     2026-10-11: Meta refused "...call us at {{2}}." with subCode 2388299 "Variables can't be at the start or end
//     of the template". The 2026-10-10 probe only tested Twilio's create step, which accepts it; Meta enforces it
//     when the template is submitted for approval. Evidence: .claude-docs/whatsapp.md (the approval ledger).
// WARN (saveable) - the other third-party "Meta body rules" (adjacent blanks, too few words per blank, over 1024
// characters) + promotional language. NOT proven enforced by anything we can check: a hard-fail would be an
// invented rule. Promote one to an error only with a ledger entry showing Meta refused it. See the plan.
//
// Kept free of Vue and axios so cypress/node-tests can execute the REAL functions.

/** The blanks a practice may use, in the words the practice reads, and the variable teamio-utility fills. */
export const TEMPLATE_VARIABLES = Object.freeze([
  Object.freeze({ name: 'site_name', label: 'Practice name' }),
  Object.freeze({ name: 'apt_date', label: 'Appointment date and time' }),
  Object.freeze({ name: 'site_officephone', label: 'Office phone' }),
]);

/** Our own input guard. */
export const HARD_MAX_CHARS = 4096;
/** The third-party body cap - a warning only. */
export const SOFT_MAX_CHARS = 1024;

// Words a practice might type that name a patient. Anything starting with "patient" is caught too.
const PATIENT_LABELS = Object.freeze(['name', 'first name', 'last name', 'full name', 'birthday', 'date of birth']);

// Whole-word promotional terms. A WARNING forever: Meta's classifier is a black box.
const PROMO_WORDS = Object.freeze([
  'discount', 'sale', 'offer', 'offers', 'promo', 'promotion', 'promotional', 'coupon', 'deal', 'deals',
  'free', 'bonus', 'exclusive', 'limited time', 'act now', 'hurry', 'special', 'save',
]);

// Messages whose patients answer "C" to confirm. For these, an edit that drops the instruction is warned about:
// an inbound reply is turned into a confirmation by the practice's own keyword list, so a message that never
// asks for one quietly stops producing confirmations. A WARNING (saveable): we cannot know the practice's wording.
const CONFIRM_KEYS = Object.freeze(['apt-reminder', 'apt-confirmation']);
const CONFIRM_REPLY_RE = /\b(reply|respond|text back|text us back|message us back)\b/i;

const BLANK_RE = /\[([^[\]]+)\]/g;
const LETTER_OR_DIGIT_RE = /[\p{L}\p{N}]/u;
// Nothing but spaces and punctuation: not a word, to WhatsApp.
const PUNCTUATION_ONLY_RE = /^[\s\p{P}]*$/u;

const normalizeLabel = (text) => String(text).trim().replace(/\s+/g, ' ').toLowerCase();

const VARIABLE_BY_LABEL = Object.freeze(
  TEMPLATE_VARIABLES.reduce((map, v) => ({ ...map, [normalizeLabel(v.label)]: v.name }), {})
);

function countWords(text) {
  return text.split(/\s+/).filter((part) => LETTER_OR_DIGIT_RE.test(part)).length;
}

function isPatientLabel(label) {
  return label.startsWith('patient') || PATIENT_LABELS.includes(label);
}

const issue = (code, message) => ({ code, message });

/**
 * Check one edited message.
 *
 * @param {*} body what the practice typed
 * @param {{ key?: string, allowedVariables?: string[] }} [options] `key`: which message this is ('apt-reminder'); only
 *        used to decide which message-specific warnings apply (e.g. a missing "Reply C to confirm" on the appointment
 *        reminder). `allowedVariables`: narrow the blanks this message may use to these variable names (a custom
 *        message may not use [Appointment date and time]: a staff reply has no single appointment). Absent = all three.
 * @returns {{ ok: boolean,
 *             errors: Array<{code: string, message: string}>,
 *             warnings: Array<{code: string, message: string}>,
 *             variables: string[],            // the variables used, in order of appearance
 *             paramMap: ?object,              // { 1: 'site_name', ... } - null unless ok
 *             numberedBody: ?string }}        // the body with {{1}}{{2}}... - null unless ok
 */
export function validateTemplateBody(body, options) {
  const key = options && typeof options.key === 'string' ? options.key : null;
  const allowed = options && Array.isArray(options.allowedVariables) ? options.allowedVariables : null;
  const text =typeof body === 'string' ? body.replace(/\r\n?/g, '\n').trim() : '';
  const errors = [];
  const warnings = [];

  if (text === '') {
    return {
      ok: false,
      errors: [issue('empty', 'Type your message first.')],
      warnings,
      variables: [],
      paramMap: null,
      numberedBody: null,
    };
  }

  if (text.length > HARD_MAX_CHARS) {
    errors.push(issue('too_long', `This message is too long. Please keep it under ${HARD_MAX_CHARS} characters.`));
  }
  if (text.includes('{{') || text.includes('}}')) {
    errors.push(issue('braces_not_allowed',
      'Please remove the double curly brackets from your message. To add your practice name, date or phone, use the buttons.'));
  }

  // Find every [blank], in order, and decide what each one is.
  const found = [];
  const reportedUnknown = new Set();
  let match;
  BLANK_RE.lastIndex = 0;
  while ((match = BLANK_RE.exec(text)) !== null) {
    const label = normalizeLabel(match[1]);
    const variable = VARIABLE_BY_LABEL[label] || null;
    found.push({ start: match.index, end: match.index + match[0].length, variable });
    if (variable && allowed && !allowed.includes(variable)) {
      // A real blank, but not one THIS message may use. Reported once per blank, like an unknown one.
      if (!reportedUnknown.has(label)) {
        reportedUnknown.add(label);
        const names = TEMPLATE_VARIABLES.filter((v) => allowed.includes(v.name)).map((v) => `[${v.label}]`);
        errors.push(issue('blank_not_allowed',
          `"[${match[1].trim()}]" can't be used in this message. ${names.length > 0 ? `You can use ${names.join(' and ')}.` : 'This message cannot have any blanks.'}`));
      }
    } else if (!variable && !reportedUnknown.has(label)) {
      reportedUnknown.add(label);
      const shown = match[1].trim();
      if (isPatientLabel(label)) {
        errors.push(issue('patient_info_not_allowed',
          `Patient names and details can't be put in WhatsApp messages. Please remove "[${shown}]".`));
      } else {
        errors.push(issue('unknown_blank',
          `"[${shown}]" isn't something we can fill in. Use one of the buttons, or remove the square brackets.`));
      }
    }
  }

  // Wording of its own: what is left once every blank is taken out.
  let wordless = '';
  let cursor = 0;
  for (const f of found) {
    wordless += `${text.slice(cursor, f.start)} `;
    cursor = f.end;
  }
  wordless += text.slice(cursor);
  const words = countWords(wordless);
  if (words === 0) {
    errors.push(issue('no_words', 'Your message needs some wording of its own, not just blanks.'));
  }

  // A blank at the very start or end of the message. WhatsApp refuses it ("Variables can't be at the start or end of
  // the template") and does NOT count spaces or punctuation as words: "...call us at {{2}}." was refused on
  // 2026-10-11 although a full stop follows the blank. So only text with a letter or digit counts. See
  // .claude-docs/whatsapp.md (the approval ledger). Anything else with no letter or digit (a symbol or emoji) is
  // unproven, so it stays a warning below.
  const startBlank = found.length > 0 ? text.slice(found[0].start, found[0].end) : '';
  const endBlank = found.length > 0 ? text.slice(found[found.length - 1].start, found[found.length - 1].end) : '';
  const beforeFirst = found.length > 0 ? text.slice(0, found[0].start) : '';
  const afterLast = found.length > 0 ? text.slice(found[found.length - 1].end) : '';
  if (found.length > 0 && PUNCTUATION_ONLY_RE.test(beforeFirst)) {
    errors.push(issue('leading_variable',
      `WhatsApp won't accept a message that starts with ${startBlank}. Please start with a few words.`));
  }
  if (found.length > 0 && PUNCTUATION_ONLY_RE.test(afterLast)) {
    errors.push(issue('trailing_variable',
      `WhatsApp won't accept a message that ends with ${endBlank}, even with a full stop after it. Please add a few words after it, such as "any time."`));
  }

  // WARNINGS - saveable, in a fixed order.
  if (found.length > 0) {
    if (!PUNCTUATION_ONLY_RE.test(beforeFirst) && !LETTER_OR_DIGIT_RE.test(beforeFirst)) {
      warnings.push(issue('leading_variable', 'WhatsApp may not approve a message that starts with a blank. Start with a few words.'));
    }
    if (!PUNCTUATION_ONLY_RE.test(afterLast) && !LETTER_OR_DIGIT_RE.test(afterLast)) {
      warnings.push(issue('trailing_variable', 'WhatsApp may not approve a message that ends with a blank. End with a few words.'));
    }
    let adjacent = false;
    for (let i = 1; i < found.length; i += 1) {
      if (text.slice(found[i - 1].end, found[i].start).trim() === '') adjacent = true;
    }
    if (adjacent) {
      warnings.push(issue('adjacent_variables', 'WhatsApp may not approve two blanks with no words between them.'));
    }
    if (words + found.length < 3 * found.length + 1) {
      warnings.push(issue('thin_wording', 'WhatsApp may not approve a message with this many blanks and so few words. Add more wording.'));
    }
  }
  if (text.length > SOFT_MAX_CHARS) {
    warnings.push(issue('over_1024_chars', `WhatsApp may not approve messages over ${SOFT_MAX_CHARS} characters.`));
  }
  const lower = text.toLowerCase();
  const promo = PROMO_WORDS.filter((w) => new RegExp(`\\b${w}\\b`).test(lower));
  if (/%\s*off/.test(lower)) promo.push('% off');
  if (promo.length > 0) {
    warnings.push(issue('promotional_language',
      `This reads like a promotion (${promo.join(', ')}). WhatsApp may treat it as an ad, which costs more per message and may not be approved.`));
  }
  if (key !== null && CONFIRM_KEYS.includes(key) && !CONFIRM_REPLY_RE.test(text)) {
    warnings.push(issue('confirm_instruction_missing',
      'This message does not ask patients to reply to confirm. Patients who reply with your confirmation word are confirmed automatically, so without a line like "Reply C to confirm" fewer patients will confirm.'));
  }

  const ok = errors.length === 0;
  const variables = found.filter((f) => f.variable).map((f) => f.variable);
  let paramMap = null;
  let numberedBody = null;
  if (ok) {
    paramMap = {};
    numberedBody = '';
    let from = 0;
    found.forEach((f, i) => {
      numberedBody += `${text.slice(from, f.start)}{{${i + 1}}}`;
      paramMap[i + 1] = f.variable;
      from = f.end;
    });
    numberedBody += text.slice(from);
  }

  return { ok, errors, warnings, variables, paramMap, numberedBody };
}
